import { Line, Bar } from "@ant-design/charts";
import { Api } from "@model";
import { Card, Col, Row, Statistic, Tabs } from "antd";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserLogTimeline } from "./UserLogTimeline";

const DashboardPage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<{
    users: number;
    unresolvedReports: number;
    unresolvedErrors: number;
    todaySuggestions: number;
    todayRegisteredUsers: number;
    todayUserMessages: number;
    todayAIMessages: number;
    paymentCount: number;
    totalWordsCount: number;
    premiumUsers: number;
    inactiveUsers: number;
    languageUsersCount: Record<string, number>;
  } | null>(null);

  useEffect(() => {
    Api.get("/api/admin/panel/stats").then((res) => setStats(res.data.data));
  }, []);

  const getLanguageName = (code: string): string => {
    const languageMap: Record<string, string> = {
      "en-us": "English",
      "es-es": "Spanish",
      "fr-fr": "French",
      "de-de": "German",
      "it-it": "Italian",
      "tr-tr": "Turkish",
      "ja-jp": "Japanese",
      "ko-kr": "Korean",
      "zh-cn": "Chinese",
      "fa-ir": "Persian",
    };
    return languageMap[code] || code;
  };

  const languageStats = stats?.languageUsersCount
    ? Object.entries(stats.languageUsersCount)
        .map(([code, count]) => ({ code, name: getLanguageName(code), count }))
        .sort((a, b) => b.count - a.count)
    : [];

  return (
    <>
      <Tabs
        defaultActiveKey="overview"
        items={[
          {
            key: "overview",
            label: "Overview",
            children: (
              <Row gutter={[6, 6]}>
                <Col span={24}>
                  <Card>
                    <h2>Overview</h2>
                    <Row gutter={[12, 12]}>
                      <Col xs={24} sm={12} md={8} lg={4}>
                        <Card>
                          <Statistic
                            title="Today Registration"
                            value={stats?.todayRegisteredUsers ?? 0}
                          />
                        </Card>
                      </Col>
                      <Col xs={24} sm={12} md={8} lg={4}>
                        <Card
                          hoverable
                          onClick={() => navigate("/panel/users")}
                        >
                          <Statistic
                            title="Total Users"
                            value={stats?.users ?? 0}
                          />
                        </Card>
                      </Col>
                      <Col xs={24} sm={12} md={8} lg={4}>
                        <Card
                          hoverable
                          onClick={() => navigate("/panel/users")}
                        >
                          <Statistic
                            title="Premium Users"
                            value={stats?.premiumUsers ?? 0}
                          />
                        </Card>
                      </Col>
                      <Col xs={24} sm={12} md={8} lg={4}>
                        <Card
                          hoverable
                          onClick={() => navigate("/panel/users")}
                        >
                          <Statistic
                            title="Inactive Users (FCM expired)"
                            value={stats?.inactiveUsers ?? 0}
                          />
                        </Card>
                      </Col>
                      <Col xs={24} sm={12} md={8} lg={4}>
                        <Card>
                          <Statistic
                            title="Today User Messages"
                            value={stats?.todayUserMessages ?? 0}
                          />
                        </Card>
                      </Col>
                      <Col xs={24} sm={12} md={8} lg={4}>
                        <Card>
                          <Statistic
                            title="Today AI Messages"
                            value={stats?.todayAIMessages ?? 0}
                          />
                        </Card>
                      </Col>
                      <Col xs={24} sm={12} md={8} lg={4}>
                        <Card
                          hoverable
                          onClick={() => navigate("/panel/settings?tab=errors")}
                        >
                          <Statistic
                            title="Unresolved Errors"
                            value={stats?.unresolvedErrors ?? 0}
                          />
                        </Card>
                      </Col>
                      <Col xs={24} sm={12} md={8} lg={4}>
                        <Card>
                          <Statistic
                            title="Today Suggestions"
                            value={stats?.todaySuggestions ?? 0}
                          />
                        </Card>
                      </Col>
                      <Col xs={24} sm={12} md={8} lg={4}>
                        <Card
                          hoverable
                          onClick={() => navigate("/panel/payments")}
                        >
                          <Statistic
                            title="Total Payments"
                            value={stats?.paymentCount ?? 0}
                          />
                        </Card>
                      </Col>
                      <Col xs={24} sm={12} md={8} lg={4}>
                        <Card
                          hoverable
                          onClick={() => navigate("/panel/reports")}
                        >
                          <Statistic
                            title="Unresolved Reports"
                            value={stats?.unresolvedReports ?? 0}
                          />
                        </Card>
                      </Col>
                      <Col xs={24} sm={12} md={8} lg={4}>
                        <Card
                          hoverable
                          onClick={() => navigate("/panel/words")}
                        >
                          <Statistic
                            title="Total Words"
                            value={stats?.totalWordsCount ?? 0}
                          />
                        </Card>
                      </Col>
                    </Row>
                  </Card>
                </Col>
                {languageStats.length > 0 && (
                  <Col xs={24} md={12}>
                    <Card>
                      <h2>Users by Language</h2>
                      <Bar
                        style={{ height: 320 }}
                        data={languageStats}
                        yField="name"
                        xField="count"
                        animation={{
                          appear: {
                            animation: "path-in",
                            duration: 2000,
                          },
                        }}
                      />
                    </Card>
                  </Col>
                )}
                <Col xs={24} md={12}>
                  <Card>
                    <h2>Most Active Users</h2>
                    <MostActiveUser />
                  </Card>
                </Col>
                <Col xs={24} sm={24} md={12}>
                  <Card>
                    <h2>Most Popular Characters</h2>
                    <MostPopularChart />
                  </Card>
                </Col>
                <Col xs={24} sm={24} md={12}>
                  <Card>
                    <h2>Installed Versions</h2>
                    <InstalledVersionsChart />
                  </Card>
                </Col>
                <Col xs={24} md={12}>
                  <Card>
                    <h2>User Account created</h2>
                    <DailyUserCreated />
                  </Card>
                </Col>
                <Col xs={24} md={12}>
                  <Card>
                    <h2>Daily AI Messages Used</h2>
                    <DailyAIMessages />
                  </Card>
                </Col>
                <Col xs={24} md={12}>
                  <Card>
                    <h2>Daily User Messages</h2>
                    <DailyUserMessages />
                  </Card>
                </Col>
                <Col xs={24} md={12}>
                  <Card>
                    <h2>Daily Runtime (Sum)</h2>
                    <DailyRuntimeSum />
                  </Card>
                </Col>
              </Row>
            ),
          },
          {
            key: "activity",
            label: "Activity",
            children: <ActivityTab />,
          },
        ]}
      />
    </>
  );
};
export default DashboardPage;

const ActivityTab = () => {
  const [logCount, setLogCount] = useState<number | null>(null);
  return (
    <Card title={logCount != null ? `Activity (${logCount})` : "Activity"}>
      <UserLogTimeline onLogCountChange={setLogCount} />
    </Card>
  );
};

const MostPopularChart = () => {
  const [mostChars, setMostChars] = useState<
    { name: string; messages: number }[]
  >([]);
  useEffect(() => {
    Api.get("/api/admin/panel/characters/most-popular").then((a) => {
      setMostChars(
        a.data.data.map((a: any) => ({
          messages: a.messageCount,
          name: `${a.name} (${a.language})`,
        })),
      );
    });
  }, []);

  return (
    <Bar
      style={{ height: 500 }}
      data={mostChars}
      yField="name"
      xField="messages"
      animation={{
        appear: {
          animation: "path-in",
          duration: 2000,
        },
      }}
    />
  );
};

const MostActiveUser = () => {
  const [data, setData] = useState<
    { name: string; count: number; category: string }[]
  >([]);
  useEffect(() => {
    Api.get("/api/admin/panel/users/most-active").then((a) => {
      const messageCount = a.data.data.map((a: any) => ({
        count: a.availableMessagesCount,
        name: a.name,
        category: "Message",
      }));
      const runTimes = a.data.data.map((a: any) => ({
        count: a.runTimes,
        name: a.name,
        category: "Run Times",
      }));
      setData([...messageCount, ...runTimes]);
    });
  }, []);

  return (
    <Bar
      style={{ height: 300 }}
      data={data}
      yField="name"
      xField="count"
      seriesField="category"
      animation={{
        appear: {
          animation: "path-in",
          duration: 2000,
        },
      }}
    />
  );
};

const DailyUserCreated = () => {
  const [data, setData] = useState<{ count: number; date: Date }[]>([]);
  useEffect(() => {
    Api.get("/api/admin/panel/users/CreateDailyUsers").then((a) => {
      const cleanedData = a.data.data
        .filter((a: any) => a._id !== null)
        .map((a: any) => ({
          count: a.count,
          date: a._id,
        }));
      setData(cleanedData);
    });
  }, []);

  return (
    <Line
      style={{ height: 300 }}
      data={data}
      xField="date"
      yField="count"
      smooth
      animation={{
        appear: {
          animation: "path-in",
          duration: 2000,
        },
      }}
    />
  );
};

const DailyAIMessages = () => {
  const [data, setData] = useState<{ count: number; date: Date }[]>([]);
  useEffect(() => {
    Api.get("/api/admin/panel/chats/daily-ai-messages").then((a) => {
      const cleanedData = a.data.data
        .filter((a: any) => a._id !== null)
        .map((a: any) => ({
          count: a.count,
          date: a._id,
        }));
      setData(cleanedData);
    });
  }, []);

  return (
    <Line
      style={{ height: 300 }}
      data={data}
      xField="date"
      yField="count"
      smooth
      animation={{
        appear: {
          animation: "path-in",
          duration: 2000,
        },
      }}
    />
  );
};

const DailyUserMessages = () => {
  const [data, setData] = useState<{ count: number; date: Date }[]>([]);
  useEffect(() => {
    Api.get("/api/admin/panel/chats/daily-user-messages").then((a) => {
      const cleanedData = a.data.data
        .filter((a: any) => a._id !== null)
        .map((a: any) => ({
          count: a.count,
          date: a._id,
        }));
      setData(cleanedData);
    });
  }, []);

  return (
    <Line
      style={{ height: 300 }}
      data={data}
      xField="date"
      yField="count"
      smooth
      animation={{
        appear: {
          animation: "path-in",
          duration: 2000,
        },
      }}
    />
  );
};

const DailyRuntimeSum = () => {
  const [data, setData] = useState<{ count: number; date: Date }[]>([]);
  useEffect(() => {
    Api.get("/api/admin/panel/users/daily-runtimes").then((a) => {
      const cleanedData = a.data.data
        .filter((a: any) => a._id !== null)
        .map((a: any) => ({
          count: a.count,
          date: a._id,
        }));
      setData(cleanedData);
    });
  }, []);

  return (
    <Line
      style={{ height: 300 }}
      data={data}
      xField="date"
      yField="count"
      smooth
      animation={{
        appear: {
          animation: "path-in",
          duration: 2000,
        },
      }}
    />
  );
};

const InstalledVersionsChart = () => {
  const [data, setData] = useState<{ version: string; count: number }[]>([]);

  const compareVersions = (a: string, b: string): number => {
    const parseVersion = (v: string): number[] => {
      return v.split(".").map(Number);
    };

    const versionA = parseVersion(a);
    const versionB = parseVersion(b);
    const maxLength = Math.max(versionA.length, versionB.length);

    for (let i = 0; i < maxLength; i++) {
      const partA = versionA[i] || 0;
      const partB = versionB[i] || 0;
      if (partA > partB) return -1;
      if (partA < partB) return 1;
    }
    return 0;
  };

  useEffect(() => {
    Api.get("/api/admin/panel/users/installed-versions").then((a) => {
      const cleanedData = a.data.data
        .filter((a: any) => a._id !== null)
        .map((a: any) => ({
          version: a._id,
          count: a.count,
        }))
        .sort(
          (
            a: { version: string; count: number },
            b: { version: string; count: number },
          ) => compareVersions(a.version, b.version),
        );
      setData(cleanedData);
    });
  }, []);

  return (
    <Bar
      style={{ height: 500 }}
      data={data}
      yField="version"
      xField="count"
      animation={{
        appear: {
          animation: "path-in",
          duration: 2000,
        },
      }}
    />
  );
};
